/**
 * Export Utilities for Analytics Data
 * Provides functions to export data in various formats (CSV, Excel, PDF)
 */

/**
 * Convert JSON data to CSV format
 */
export const convertToCSV = (data: any[]): string => {
  if (!data || data.length === 0) return '';

  // Get headers from first object
  const headers = Object.keys(data[0]);

  // Create CSV header row
  const headerRow = headers.join(',');

  // Create data rows
  const dataRows = data.map(item => {
    return headers.map(header => {
      const value = item[header];

      // Handle different data types
      if (value === null || value === undefined) {
        return '';
      }

      // Convert to string and escape quotes
      const stringValue = String(value).replace(/"/g, '""');

      // Wrap in quotes if contains comma, newline, or quote
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue}"`;
      }

      return stringValue;
    }).join(',');
  });

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Convert object to CSV (for summary data)
 */
export const objectToCSV = (data: Record<string, any>): string => {
  const rows = Object.entries(data).map(([key, value]) => {
    // Handle nested objects
    if (typeof value === 'object' && value !== null) {
      return `"${key}","${JSON.stringify(value)}"`;
    }
    return `"${key}","${value}"`;
  });

  return 'Key,Value\n' + rows.join('\n');
};

/**
 * Download data as CSV file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export analytics data to CSV
 */
export const exportAnalyticsToCSV = (data: any, dataType: string): void => {
  let csvContent: string;
  let filename: string;

  if (Array.isArray(data)) {
    csvContent = convertToCSV(data);
    filename = `${dataType}_data`;
  } else if (typeof data === 'object') {
    csvContent = objectToCSV(data);
    filename = `${dataType}_summary`;
  } else {
    console.error('Invalid data format for CSV export');
    return;
  }

  downloadCSV(csvContent, filename);
};

/**
 * Export table data to CSV with custom headers
 */
export const exportTableToCSV = (
  headers: string[],
  rows: any[][],
  filename: string
): void => {
  const csvRows = [
    headers.join(','),
    ...rows.map(row =>
      row.map(cell => {
        const stringValue = String(cell || '').replace(/"/g, '""');
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue}"`;
        }
        return stringValue;
      }).join(',')
    )
  ];

  downloadCSV(csvRows.join('\n'), filename);
};

/**
 * Export chart data to CSV
 */
export const exportChartDataToCSV = (
  chartData: any[],
  xKey: string,
  dataKeys: string[],
  filename: string
): void => {
  const headers = [xKey, ...dataKeys];
  const rows = chartData.map(item => [
    item[xKey],
    ...dataKeys.map(key => item[key] || 0)
  ]);

  exportTableToCSV(headers, rows, filename);
};

/**
 * Download an array of objects as an .xlsx file using SheetJS.
 *
 * First object's keys become the header row. Rows with missing keys
 * leave the corresponding cells blank — matches the rectangular CSV
 * behavior of `downloadCSV` so callers can switch formats without
 * reshaping their data.
 *
 * Example:
 *   const rows = await fetch('/api/outreach-activities').then(r => r.json());
 *   downloadXLSX(rows, 'outreach-activities', 'Outreach');
 */
export const downloadXLSX = async (
  rows: Record<string, any>[],
  filename: string,
  sheetName: string = 'Sheet1'
): Promise<void> => {
  // Lazy-load SheetJS so callers that never export don't pull the library
  // into their initial bundle. This used to be a bare `require('xlsx')`,
  // which Vite leaves untouched in an ES module, so the production bundle
  // threw "require is not defined" on every click. A dynamic import() is
  // the form Vite actually code-splits.
  const XLSX = await import('xlsx');

  const worksheet = XLSX.utils.json_to_sheet(rows ?? []);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31)); // Excel sheet name limit

  const datestamp = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `${filename}_${datestamp}.xlsx`, { compression: true });
};

/**
 * Download a table-shaped dataset as .xlsx. Mirrors `exportTableToCSV`
 * so the two formats stay interchangeable in call sites.
 */
export const exportTableToXLSX = async (
  headers: string[],
  rows: any[][],
  filename: string,
  sheetName: string = 'Sheet1'
): Promise<void> => {
  const XLSX = await import('xlsx');

  const data: any[][] = [headers, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));

  const datestamp = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `${filename}_${datestamp}.xlsx`, { compression: true });
};

/**
 * Batch export multiple datasets
 */
export const batchExportToCSV = (
  datasets: Array<{ data: any; filename: string }>,
  zipFilename?: string
): void => {
  // Export each dataset separately
  // In a production app, you might want to zip these together
  datasets.forEach(({ data, filename }) => {
    exportAnalyticsToCSV(data, filename);
  });

  // Note: Actual ZIP implementation would require a library like JSZip
  if (zipFilename) {
    console.log(`Note: ZIP export would create ${zipFilename}.zip with all datasets`);
  }
};

/**
 * Format numbers for export
 */
export const formatNumberForExport = (value: number, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return value.toFixed(decimals);
};

/**
 * Format dates for export
 */
export const formatDateForExport = (date: Date | string): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
};

/**
 * Flatten nested objects for CSV export
 */
export const flattenObject = (obj: any, prefix: string = ''): Record<string, any> => {
  const flattened: Record<string, any> = {};

  Object.keys(obj).forEach(key => {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, newKey));
    } else if (Array.isArray(value)) {
      flattened[newKey] = value.join('; ');
    } else {
      flattened[newKey] = value;
    }
  });

  return flattened;
};

/**
 * Export with custom formatting
 */
export const exportWithFormatting = (
  data: any[],
  columnFormatters: Record<string, (value: any) => string>,
  filename: string
): void => {
  const formattedData = data.map(item => {
    const formatted: any = {};
    Object.keys(item).forEach(key => {
      if (columnFormatters[key]) {
        formatted[key] = columnFormatters[key](item[key]);
      } else {
        formatted[key] = item[key];
      }
    });
    return formatted;
  });

  const csvContent = convertToCSV(formattedData);
  downloadCSV(csvContent, filename);
};
